import { Component } from '@theme/component';

/** Every step, in order. */
const ORDER = ['type', 'items', 'details', 'info', 'source', 'review'];
/** Where the answers wait while Shopify checks the form, so an error does not lose them. */
const STORAGE_KEY = 'quote-builder-answers';
/** A real person cannot finish the steps this fast. */
const MINIMUM_SECONDS = 3;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * @typedef {object} Answers
 * @property {string} type - The customer type.
 * @property {string[]} items - What they are looking for, in the order chosen.
 * @property {Record<string, string>} quantities - Item name to the quantity typed ('' = not sure).
 * @property {string} idea
 * @property {string} name
 * @property {string} business
 * @property {string} email
 * @property {string} phone
 * @property {string} date - YYYY-MM-DD, or ''.
 * @property {string} source
 * @property {string} sourceOther
 */

/**
 * @typedef {object} QuoteBuilderRefs
 * @property {HTMLElement} [thanks]
 * @property {HTMLElement} [intro]
 * @property {HTMLElement} [serverError]
 * @property {HTMLElement} [fallback]
 * @property {HTMLElement} [app]
 * @property {HTMLElement} [stage]
 * @property {HTMLElement} [chips]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [progress]
 * @property {HTMLElement} [quantities]
 * @property {HTMLElement} [quantitiesHelp]
 * @property {HTMLTemplateElement} [quantityTemplate]
 * @property {HTMLTextAreaElement} [idea]
 * @property {HTMLInputElement} [name]
 * @property {HTMLInputElement} [business]
 * @property {HTMLElement} [businessOptional]
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [phone]
 * @property {HTMLInputElement} [date]
 * @property {HTMLElement} [sourceOtherField]
 * @property {HTMLInputElement} [sourceOther]
 * @property {HTMLElement} [summary]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [sending]
 * @property {HTMLButtonElement} [back]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLTextAreaElement} [body]
 * @property {HTMLInputElement} [trap]
 */

/**
 * Step-by-step quote request, in the style of the hat, apparel and signs builders. It
 * sends Shopify's contact form: the name, email and phone go in their own fields and every
 * answer is written into the message body, one labeled line each. Those labels are fixed
 * English because the email is read by the store.
 *
 * After Shopify accepts the form the section renders only the thank-you message, so every
 * ref is optional.
 *
 * @extends {Component<QuoteBuilderRefs>}
 */
class QuoteBuilder extends Component {
  #current = 0;
  #type = '';
  /** @type {string[]} */
  #items = [];
  /** @type {Record<string, string>} */
  #quantities = {};
  #source = '';
  #started = Date.now();
  #sending = false;
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    const { app, stage, fallback, date, thanks } = this.refs;
    const form = this.querySelector('form');

    // Sent: only the thank-you message is on the page.
    if (!app || !stage || !form) {
      this.#forget();
      if (thanks && !thanks.hidden) thanks.focus({ preventScroll: true });
      return;
    }

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('input', this.#onInput, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('keydown', this.#onKeydown, { signal });
    // Coming back with the browser's back button: allow sending again.
    window.addEventListener('pageshow', () => this.#setSending(false), { signal });

    this.#started = Date.now();
    if (date) date.min = this.#today();
    if (fallback) fallback.hidden = true;
    app.hidden = false;

    // Shopify sent the form back with an error: put the answers back and show the review.
    if (stage.hasAttribute('data-restore') && this.#restore()) {
      this.#current = ORDER.length - 1;
      // They already spent the time on the first try, so sending again straight away is fine.
      this.#started = 0;
      this.#render(false);
      this.refs.serverError?.focus();
      return;
    }
    this.#forget();
    this.#render(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** @returns {string} Today's date where the customer is, as YYYY-MM-DD. */
  #today() {
    const now = new Date();
    const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  /**
   * @param {string | undefined} list - A comma-separated setting.
   * @returns {string[]} Its entries in lower case.
   */
  #names(list) {
    return (list ?? '')
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);
  }

  /** @returns {boolean} True when the chosen customer type may leave the business name empty. */
  #businessOptional() {
    return this.#names(this.dataset.businessOptional).includes(this.#type.toLowerCase());
  }

  /** @returns {string[]} The chosen items that get a quantity box: all but "not sure". */
  #countedItems() {
    const unsure = this.#names(this.dataset.unsureItem);
    return this.#items.filter((item) => !unsure.includes(item.toLowerCase()));
  }

  /** @returns {boolean} True when the chosen source asks for details. */
  #sourceNeedsDetails() {
    return this.#source !== '' && this.#names(this.dataset.otherSource).includes(this.#source.toLowerCase());
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]} Every tile of a step.
   */
  #tiles(key) {
    return Array.from(this.querySelectorAll(`.hat-builder__tile[data-step="${key}"]`));
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #panel(key) {
    return this.querySelector(`[data-panel="${key}"]`);
  }

  /** @returns {Answers} Everything answered so far. */
  #answers() {
    const refs = this.refs;
    const value = (/** @type {{ value: string } | undefined} */ field) => (field?.value ?? '').trim();
    return {
      type: this.#type,
      items: [...this.#items],
      quantities: Object.fromEntries(this.#countedItems().map((item) => [item, (this.#quantities[item] ?? '').trim()])),
      idea: value(refs.idea),
      name: value(refs.name),
      business: value(refs.business),
      email: value(refs.email),
      phone: value(refs.phone),
      date: value(refs.date),
      source: this.#source,
      sourceOther: this.#sourceNeedsDetails() ? value(refs.sourceOther) : '',
    };
  }

  /**
   * Checks one step.
   * @param {string} key
   * @returns {Record<string, string>} Field name to error message; empty when the step is fine.
   */
  #problems(key) {
    const data = this.dataset;
    const answers = this.#answers();
    /** @type {Record<string, string>} */
    const problems = {};

    if (key === 'type' && !answers.type) problems.type = data.errorType ?? '';
    if (key === 'items' && !answers.items.length) problems.items = data.errorItems ?? '';
    if (key === 'details') {
      for (const [item, quantity] of Object.entries(answers.quantities)) {
        if (quantity !== '' && !/^\d+$/.test(quantity)) problems[`quantity:${item}`] = data.errorQuantity ?? '';
      }
    }
    if (key === 'info') {
      if (!answers.name) problems.name = data.errorName ?? '';
      if (!answers.business && !this.#businessOptional()) problems.business = data.errorBusiness ?? '';
      if (!EMAIL_PATTERN.test(answers.email)) problems.email = data.errorEmail ?? '';
      // A date typed by hand can be in the past or incomplete; the picker alone does not stop that.
      const date = this.refs.date;
      if (date && (date.validity.badInput || (answers.date !== '' && answers.date < this.#today()))) problems.date = data.errorDate ?? '';
    }
    return problems;
  }

  /**
   * Shows or clears the messages of a step. A message that appears is announced.
   * @param {string} key
   * @param {Record<string, string>} problems
   * @returns {HTMLElement | null} The first field with a problem, to move focus to.
   */
  #showProblems(key, problems) {
    const panel = this.#panel(key);
    if (!panel) return null;
    /** @type {HTMLElement | null} */
    let first = null;

    const set = (/** @type {Element | null} */ message, /** @type {HTMLElement | null} */ field, /** @type {string} */ text) => {
      if (!(message instanceof HTMLElement)) return;
      message.textContent = text;
      message.hidden = !text;
      if (field instanceof HTMLInputElement) field.setAttribute('aria-invalid', String(Boolean(text)));
      if (text && !first) first = field ?? panel.querySelector('.hat-builder__tile');
    };

    for (const message of panel.querySelectorAll('[data-error-for]')) {
      const name = message instanceof HTMLElement ? message.dataset.errorFor ?? '' : '';
      const field = /** @type {Record<string, HTMLElement | undefined>} */ (this.refs)[name] ?? null;
      set(message, field, problems[name] ?? '');
    }
    for (const row of panel.querySelectorAll('[data-quantity]')) {
      if (!(row instanceof HTMLElement)) continue;
      const input = row.querySelector('[data-quantity-input]');
      set(row.querySelector('[data-quantity-error]'), input instanceof HTMLElement ? input : null, problems[`quantity:${row.dataset.quantity}`] ?? '');
    }
    return first;
  }

  /**
   * @param {string} key
   * @returns {boolean} True when the step is fine. Otherwise its messages are shown and focus moves to the first one.
   */
  #check(key) {
    const problems = this.#problems(key);
    const first = this.#showProblems(key, problems);
    if (!Object.keys(problems).length) return true;
    first?.focus();
    return false;
  }

  /** One quantity box per chosen item. What was typed for an item is kept. */
  #buildQuantities() {
    const { quantities, quantityTemplate, quantitiesHelp, idea } = this.refs;
    if (!quantities || !quantityTemplate) return;
    const items = this.#countedItems();
    const built = Array.from(quantities.children).map((row) => (row instanceof HTMLElement ? row.dataset.quantity : ''));
    if (quantitiesHelp) quantitiesHelp.hidden = items.length === 0;
    if (built.join('\n') === items.join('\n')) return;

    quantities.replaceChildren(
      ...items.map((item, index) => {
        const row = /** @type {HTMLElement} */ (quantityTemplate.content.firstElementChild?.cloneNode(true));
        const label = row.querySelector('[data-quantity-label]');
        const input = row.querySelector('[data-quantity-input]');
        const message = row.querySelector('[data-quantity-error]');
        row.dataset.quantity = item;
        if (input instanceof HTMLInputElement && label instanceof HTMLLabelElement && message instanceof HTMLElement) {
          input.id = `${idea?.id ?? 'quote'}-quantity-${index}`;
          input.value = this.#quantities[item] ?? '';
          label.htmlFor = input.id;
          label.textContent = item;
          message.id = `${input.id}-error`;
          input.setAttribute('aria-describedby', message.id);
        }
        return row;
      })
    );
  }

  /**
   * The answers as short text per step, for the selections and the review.
   * @returns {Record<string, string[]>}
   */
  #lines() {
    const answers = this.#answers();
    const notSure = this.dataset.notSure ?? '';
    const label = (/** @type {HTMLElement | undefined} */ field) => {
      const element = field ? this.querySelector(`label[for="${field.id}"]`) : null;
      // The label's own words, without the "(optional)" note inside it.
      return element ? (element.firstChild?.textContent ?? '').trim() : '';
    };
    const pair = (/** @type {string} */ name, /** @type {string} */ value) => (value ? [`${name}: ${value}`] : []);
    const refs = this.refs;

    return {
      type: answers.type ? [answers.type] : [],
      items: answers.items.length ? [answers.items.join(', ')] : [],
      details: [...Object.entries(answers.quantities).map(([item, quantity]) => `${item}: ${quantity || notSure}`), ...(answers.idea ? [answers.idea] : [])],
      info: [
        ...pair(label(refs.name), answers.name),
        ...pair(label(refs.business), answers.business),
        ...pair(label(refs.email), answers.email),
        ...pair(label(refs.phone), answers.phone),
        ...pair(label(refs.date), this.#readableDate(answers.date)),
      ],
      source: answers.source ? [answers.sourceOther ? `${answers.source}: ${answers.sourceOther}` : answers.source] : [],
    };
  }

  /**
   * @param {string} date - YYYY-MM-DD.
   * @param {string} [locale]
   * @returns {string} For example "November 5, 2026".
   */
  #readableDate(date, locale) {
    const [year, month, day] = date.split('-').map(Number);
    if (!year || !month || !day) return '';
    return new Date(year, month - 1, day).toLocaleDateString(locale || document.documentElement.lang || undefined, { dateStyle: 'long' });
  }

  /** @param {boolean} moved - True when the step changed, to slide it in and move focus. */
  #render(moved) {
    const refs = this.refs;
    const key = ORDER[this.#current] ?? 'type';
    if (key === 'details') this.#buildQuantities();

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.panel === key;
      panel.hidden = !active;
      panel.removeAttribute('data-enter');
      if (active && moved) {
        // Restart the slide-in animation.
        void panel.offsetWidth;
        panel.setAttribute('data-enter', '');
        const heading = panel.querySelector('.quote-builder__panel-heading');
        if (heading instanceof HTMLElement) heading.focus();
      }
    }

    for (const tile of this.#tiles('type')) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#type));
    for (const tile of this.#tiles('items')) tile.setAttribute('aria-pressed', String(this.#items.includes(tile.dataset.value ?? '')));
    for (const tile of this.#tiles('source')) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#source));

    if (refs.sourceOtherField) refs.sourceOtherField.hidden = !this.#sourceNeedsDetails();
    const optional = this.#businessOptional();
    if (refs.businessOptional) {
      refs.businessOptional.textContent = optional ? this.dataset.optional ?? '' : '';
      refs.businessOptional.hidden = !optional;
    }
    refs.business?.setAttribute('aria-required', String(!optional));

    const lines = this.#lines();
    this.#renderChips(lines);
    if (key === 'review') this.#renderSummary(lines);

    refs.progress?.replaceChildren(
      ...ORDER.map((step, index) => {
        const segment = document.createElement('span');
        if (index < this.#current) segment.setAttribute('data-done', '');
        return segment;
      })
    );

    if (refs.back) refs.back.disabled = this.#current === 0;
    // The review step ends with the send button instead.
    if (refs.next) refs.next.hidden = key === 'review';
  }

  /**
   * The customer's answers so far. Tapping one goes back to that step.
   * @param {Record<string, string[]>} lines
   */
  #renderChips(lines) {
    const { chips, empty } = this.refs;
    if (!chips) return;
    const built = [];
    for (const key of ORDER) {
      const full = (lines[key] ?? []).join(' · ');
      if (!full) continue;
      // The selections stay short; the review shows everything.
      const text = full.length > 80 ? `${full.slice(0, 77).trimEnd()}…` : full;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'quote-builder__chip';
      chip.dataset.goto = key;

      const label = document.createElement('span');
      label.className = 'quote-builder__chip-label';
      label.textContent = this.#panel(key)?.dataset.label ?? '';
      const value = document.createElement('span');
      value.className = 'quote-builder__chip-value';
      value.textContent = text;
      chip.append(label, value);
      built.push(chip);
    }
    chips.replaceChildren(...built);
    if (empty) empty.hidden = built.length > 0;
  }

  /**
   * The review: every step's answers with a button to change them.
   * @param {Record<string, string[]>} lines
   */
  #renderSummary(lines) {
    const { summary } = this.refs;
    if (!summary) return;
    summary.replaceChildren(
      ...ORDER.filter((key) => key !== 'review').map((key) => {
        const name = this.#panel(key)?.dataset.label ?? '';
        const group = document.createElement('div');
        group.className = 'quote-builder__summary-group';

        const title = document.createElement('h3');
        title.className = 'quote-builder__summary-title';
        title.textContent = name;

        const list = document.createElement('ul');
        list.className = 'quote-builder__summary-list';
        const entries = lines[key]?.length ? lines[key] : ['—'];
        for (const entry of entries) {
          const item = document.createElement('li');
          item.textContent = entry;
          list.append(item);
        }

        const edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'button-secondary quote-builder__edit';
        edit.dataset.goto = key;
        edit.textContent = this.dataset.editLabel ?? '';
        edit.setAttribute('aria-label', `${this.dataset.editLabel ?? ''}: ${name}`);

        group.append(title, list, edit);
        return group;
      })
    );
  }

  /** @param {number} index */
  #go(index) {
    this.#current = Math.min(ORDER.length - 1, Math.max(0, index));
    this.#render(true);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { back, next } = this.refs;

    const tile = target.closest('.hat-builder__tile');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      const step = tile.dataset.step ?? '';
      const value = tile.dataset.value ?? '';
      if (step === 'type') {
        // One answer: choosing it moves on.
        this.#type = value;
        this.#showProblems('type', {});
        this.#go(ORDER.indexOf('type') + 1);
      } else if (step === 'items') {
        // Several answers: a tap adds or removes one.
        this.#items = this.#items.includes(value) ? this.#items.filter((item) => item !== value) : [...this.#items, value];
        if (this.#items.length) this.#showProblems('items', {});
        this.#render(false);
      } else if (step === 'source') {
        // A second tap takes the answer back; the question is optional.
        this.#source = this.#source === value ? '' : value;
        this.#render(false);
        if (this.#sourceNeedsDetails()) this.refs.sourceOther?.focus();
      }
      return;
    }

    const jump = target.closest('[data-goto]');
    if (jump instanceof HTMLElement) {
      this.#go(ORDER.indexOf(jump.dataset.goto ?? ''));
      return;
    }

    if (back?.contains(target)) {
      this.#go(this.#current - 1);
      return;
    }
    if (next?.contains(target) && this.#check(ORDER[this.#current] ?? '')) this.#go(this.#current + 1);
  };

  /**
   * Typing clears that field's message once it is fixed, and updates the selections.
   * @param {Event} event
   */
  #onInput = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-quantity-input]')) {
      const row = input.closest('[data-quantity]');
      if (row instanceof HTMLElement) this.#quantities[row.dataset.quantity ?? ''] = input.value;
    }
    const key = ORDER[this.#current] ?? '';
    const panel = this.#panel(key);
    // Only refresh messages that are already showing; nothing new appears while typing.
    if (panel?.querySelector('[role="alert"]:not([hidden])')) {
      const problems = this.#problems(key);
      const shown = Object.fromEntries(
        Object.entries(problems).filter(([name]) => {
          const row = Array.from(panel.querySelectorAll('[data-quantity]')).find(
            (candidate) => candidate instanceof HTMLElement && `quantity:${candidate.dataset.quantity}` === name
          );
          const message = row ? row.querySelector('[data-quantity-error]') : panel.querySelector(`[data-error-for="${name}"]`);
          return message instanceof HTMLElement && !message.hidden;
        })
      );
      this.#showProblems(key, shown);
    }
    this.#renderChips(this.#lines());
  };

  /**
   * Enter in a field goes to the next step instead of sending a half-finished form.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    this.refs.next?.click();
  };

  /** @param {boolean} sending */
  #setSending(sending) {
    this.#sending = sending;
    const { submit, sending: status } = this.refs;
    if (submit) submit.setAttribute('aria-disabled', String(sending));
    if (status) status.textContent = sending ? status.dataset.sending ?? '' : '';
  }

  /** @returns {boolean} True when saved answers were put back. */
  #restore() {
    try {
      const saved = /** @type {Answers | null} */ (JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? 'null'));
      if (!saved || typeof saved !== 'object') return false;
      const refs = this.refs;
      const known = (/** @type {string} */ step, /** @type {unknown} */ value) =>
        typeof value === 'string' && this.#tiles(step).some((tile) => tile.dataset.value === value);

      this.#type = known('type', saved.type) ? saved.type : '';
      this.#items = Array.isArray(saved.items) ? saved.items.filter((item) => known('items', item)) : [];
      this.#source = known('source', saved.source) ? saved.source : '';
      this.#quantities = saved.quantities && typeof saved.quantities === 'object' ? { ...saved.quantities } : {};
      const fill = (/** @type {{ value: string } | undefined} */ field, /** @type {unknown} */ value) => {
        if (field && typeof value === 'string') field.value = value;
      };
      fill(refs.idea, saved.idea);
      fill(refs.name, saved.name);
      fill(refs.business, saved.business);
      fill(refs.email, saved.email);
      fill(refs.phone, saved.phone);
      fill(refs.date, saved.date);
      fill(refs.sourceOther, saved.sourceOther);
      return true;
    } catch {
      return false;
    }
  }

  #forget() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage can be blocked; there is nothing to forget then.
    }
  }

  /**
   * The message the store receives: a subject line, then one labeled line per answer.
   * @param {Answers} answers
   * @returns {string}
   */
  #message(answers) {
    const lines = [`Quote request: ${answers.business || answers.name}`, ''];
    const add = (/** @type {string} */ label, /** @type {string} */ value) => {
      if (value) lines.push(`${label}: ${value}`);
    };
    add('Customer type', answers.type);
    add('Business or team name', answers.business);
    add('Name', answers.name);
    add('Email', answers.email);
    add('Phone', answers.phone);
    add('Looking for', answers.items.join(', '));
    for (const [item, quantity] of Object.entries(answers.quantities)) add(`Quantity - ${item}`, quantity || 'Not sure');
    add('Their idea', answers.idea.replace(/\s*\n\s*/g, ' / '));
    add('Date needed', answers.date ? `${this.#readableDate(answers.date, 'en-US')} (${answers.date})` : '');
    add('Heard about us', answers.sourceOther ? `${answers.source} - ${answers.sourceOther}` : answers.source);
    return lines.join('\n');
  }

  /** Shows the thank-you message without sending anything. */
  #pretendSent() {
    const { app, thanks, intro, serverError } = this.refs;
    if (app) app.hidden = true;
    if (intro) intro.hidden = true;
    if (serverError) serverError.hidden = true;
    if (thanks) {
      thanks.hidden = false;
      thanks.focus();
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (this.#sending) {
      event.preventDefault();
      return;
    }

    // Every step is checked again; the first one with a problem is shown.
    for (const key of ORDER) {
      if (Object.keys(this.#problems(key)).length === 0) continue;
      event.preventDefault();
      this.#current = ORDER.indexOf(key);
      this.#render(true);
      this.#check(key);
      return;
    }

    // Spam checks. A person never fills the hidden field and cannot finish this quickly.
    const tooFast = Date.now() - this.#started < MINIMUM_SECONDS * 1000;
    if ((this.refs.trap?.value ?? '') !== '' || tooFast) {
      event.preventDefault();
      this.#pretendSent();
      return;
    }

    const answers = this.#answers();
    if (this.refs.body) this.refs.body.value = this.#message(answers);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
    } catch {
      // Storage can be blocked; the form is still sent.
    }
    this.#setSending(true);
  };
}

if (!customElements.get('quote-builder')) {
  customElements.define('quote-builder', QuoteBuilder);
}
